import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import App from './App';
import './index.css';
import './i18n'; // Initialize i18n
import { logger } from '@/utils/logger';
import { OidcProvider } from '@/auth/OidcProvider';
import { bindQueryCacheToAuth } from '@/utils/queryCacheAuth';

// PWA Components
import { InstallPrompt } from './components/InstallPrompt';
import { UpdateNotification } from './components/UpdateNotification';
import { OfflineIndicator } from './components/OfflineIndicator';

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes - consider data fresh
      gcTime: 10 * 60 * 1000, // 10 minutes - keep unused data in cache
      refetchOnMount: 'always', // Refetch on component mount if data is stale
      refetchOnReconnect: true, // Refetch when network reconnects
    },
    mutations: {
      retry: 1,
      // Add error handling for mutations
      onError: (error: any) => {
        logger.error('Mutation error:', error);
      },
    },
  },
});

// Cached server data belongs to one signed-in identity: drop it on logout or account switch.
bindQueryCacheToAuth(queryClient);

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  const tracesSampleRate = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.0');
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0.0,
    enabled: true,
  });
}

/**
 * PWA Provider Component
 * Wraps the app with PWA-related UI components
 */
function PWAProvider({ children }: { children: React.ReactNode }) {
  return (
    <>
      {children}
      {/* PWA Install Prompt - shows after 3 seconds if installable */}
      <InstallPrompt delay={3000} />
      {/* Update Notification - shows when new version is available */}
      <UpdateNotification />
      {/* Offline Indicator - shows when user loses connection */}
      <OfflineIndicator />
    </>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <Sentry.ErrorBoundary fallback={<div>Something went wrong.</div>}>
        <OidcProvider>
          <PWAProvider>
            <App />
          </PWAProvider>
        </OidcProvider>
      </Sentry.ErrorBoundary>
    </QueryClientProvider>
  </React.StrictMode>
);
